import { gql, useMutation } from '@apollo/client'
import { useState } from 'react'

import {
  SendVerificationEmail,
  SendVerificationEmailMutation,
  SendVerificationEmailMutationVariables,
  UpdateUserEmailMutation,
  UpdateUserEmailMutationVariables,
} from '@/clients/utoniq-core/schema'
import { useAuth, useMessage, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { setMailInput } from '@/core/native/storage'

import { Action, State } from './template'
import type { EmailAddressFormValues } from './template/EmailAddressInputForm'
/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const [step, nextStep] = useState(0)
  const [formValues, setFormValues] = useState<EmailAddressFormValues>({
    email: '',
    password: '',
  })
  const { user, updateEmail, isBoundWithSNS } = useAuth()
  const { router, routerPages } = useRouter()
  const { showError } = useMessage()
  const [sendVerifyEmail, { loading: verifyEmailSending }] = useMutation<
    SendVerificationEmailMutation,
    SendVerificationEmailMutationVariables
  >(SendVerificationEmail)
  const [updateUserEmail] = useMutation<
    UpdateUserEmailMutation,
    UpdateUserEmailMutationVariables
  >(UpdateUserEmail)

  return {
    state: {
      step,
      email: user?.email || '',
      isBoundWithSNS,
      verifyEmailSending,
    },
    actions: {
      // step0ではpasswordは使わないが、step1で使うため、stateに保存しておく
      sendVerificationMail: async ({
        email,
        password,
      }: EmailAddressFormValues) => {
        await sendVerifyEmail({ variables: { email } })
          .then(async () => {
            // sendVerifyEmailで認証コード送信が完了してから、フォームに入力したemailとpasswordをstateに保存する
            setFormValues({ email, password })
            // step1に進めて、認証コード入力フォームを表示する
            nextStep(1)
          })
          .catch(showError)
      },
      sendVerificationMailSocial: async ({ email }) => {
        await sendVerifyEmail({ variables: { email } })
          .then(async () => {
            await setMailInput(email)
            router.push(routerPages.MailAuth)
          })
          .catch(showError)
      },
      resendVerificationMail: async () => {
        const { email } = formValues
        await sendVerifyEmail({ variables: { email } }).catch(showError)
      },
      updateEmail: async ({ code }) => {
        // step0で入力済みのemail+passwordと、step1で入力したcodeを使用して、メールアドレスを更新する
        // passwordは更新後のメールアドレスで再認証するために渡す
        const { email, password } = formValues
        return updateEmail(email, password, async (inputEmail: string) => {
          await updateUserEmail({
            variables: { inputs: { email: inputEmail, code } },
          })
            .then(() => {
              // メールアドレス更新後、stepを2に進めて更新完了ダイアログを表示する
              nextStep(2)
            })
            .catch(e => {
              throw e
            })
        })
      },
      onCloseCompleteModal: () => {
        router.push(routerPages.AccountSetting)
      },
    },
  }
})

const UpdateUserEmail = gql`
  mutation updateUserEmail($inputs: UpdateUserEmailInput) {
    updateUserEmail(inputs: $inputs) {
      id
      email
    }
  }
`
